import React, { useState, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { FiX, FiCheck, FiAlertCircle, FiArrowLeft, FiRefreshCw } from 'react-icons/fi';
import axios from 'axios';
import { BASE_URL } from '../constants/config';
import { getTokensFromStore, checkIfTokenExpired } from '../utilities/tokens';
import { refreshTokens } from '../actions/usersActions';
import SelectSearch from 'react-select-search';
import Fuse from 'fuse.js';
import moment from 'moment';
import { planStage, findConflicts, stageIdOf } from '../utilities/tourGenerator';
import '../styles/createMatchModal.css';

/**
 * GenerateTourModal — генерация матчей для стадии (тура)
 *
 * Пары подбирает utilities/tourGenerator.js:
 * - в пределах круга пара не повторяется (кто дома — неважно);
 * - уже созданные матчи тура учитываются, занятые команды не трогаются;
 * - групповая лига (по настройкам сезона): пары только внутри групп league_groups;
 * - перед сохранением всё перепроверяется по свежим данным.
 *
 * format — { leagueType, groupStagesAmount } из настроек сезона,
 * stages — стадии сезона (нужны их priority).
 */
function GenerateTourModal({ isOpen, onClose, league, season, stage, onSuccess, format = null, stages = [] }) {
  const dispatch = useDispatch();

  // Шаг: 'settings' или 'preview'
  const [step, setStep] = useState('settings');

  // Настройки матчей
  const [matchDate, setMatchDate] = useState(moment().format('YYYY-MM-DD'));
  const [matchTime, setMatchTime] = useState('');
  const [selectedStadium, setSelectedStadium] = useState('');
  const [selectedJudge, setSelectedJudge] = useState('');
  const [halfTime, setHalfTime] = useState('');

  // Справочники
  const [stadiums, setStadiums] = useState([]);
  const [judges, setJudges] = useState([]);

  // Данные для генерации
  const [existingMatches, setExistingMatches] = useState([]);

  // Превью
  const [generatedMatches, setGeneratedMatches] = useState([]);
  const [disabledMatches, setDisabledMatches] = useState(new Set());
  // Данные, по которым построено превью, и номер варианта пар
  const [planData, setPlanData] = useState(null);
  const [variant, setVariant] = useState(0);
  const [variantsCount, setVariantsCount] = useState(0);
  const [planNotes, setPlanNotes] = useState([]);

  // UI
  const [generating, setGenerating] = useState(false);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState({ text: '', type: '' });

  useEffect(() => {
    if (isOpen) {
      resetForm();
      loadInitialData();
    }
  }, [isOpen]);

  const loadInitialData = async () => {
    // Загружаем справочники (стадионы, судьи) — они нужны для формы
    // Остальное (команды, матчи, группы) загружаем параллельно,
    // но не блокируем UI — генерация сама перезагрузит при необходимости
    try {
      await Promise.all([
        loadStadiums(),
        loadJudges()
      ]);
    } catch (error) {
      console.error('Error loading stadiums/judges:', error);
    }
    // Некритичные данные — загружаем в фоне, не блокируя
    Promise.all([
      loadExistingMatches()
    ]).catch(err => console.error('Error loading background data:', err));
  };

  const getAuthHeaders = async () => {
    const isTokenExpired = checkIfTokenExpired();
    if (isTokenExpired) {
      await dispatch(refreshTokens());
    }
    const tokens = getTokensFromStore();
    return {
      Authorization: "Bearer " + tokens.access_token,
      Accept: 'application/json',
      'Content-Type': 'application/json',
    };
  };

  const loadStadiums = async () => {
    try {
      const headers = await getAuthHeaders();
      const response = await axios.post(
        BASE_URL.url + '/stadiums/getAllStadiums',
        {},
        { headers }
      );
      const stadiumsData = Array.isArray(response.data) ? response.data : (response.data.data || []);
      setStadiums(stadiumsData.map(s => ({ name: s.name, value: s.id })));

      if (league?.cache_stadium?.id) {
        setSelectedStadium(league.cache_stadium.id);
      }
    } catch (error) {
      console.error('Error loading stadiums:', error);
    }
  };

  const loadJudges = async () => {
    try {
      const headers = await getAuthHeaders();
      const response = await axios.post(
        BASE_URL.url + '/judges/getAllReferees',
        {},
        { headers }
      );
      const judgesData = Array.isArray(response.data) ? response.data : (response.data.data || []);
      setJudges(judgesData.map(j => ({
        name: `${j.name || ''} ${j.family || ''}`.trim(),
        value: j.id
      })));

      if (league?.cache_ref?.length > 0 && league.cache_ref[0].id) {
        setSelectedJudge(league.cache_ref[0].id);
      }
    } catch (error) {
      console.error('Error loading judges:', error);
    }
  };

  const loadExistingMatches = async () => {
    if (!league || !season || !stage) return;
    try {
      const headers = await getAuthHeaders();
      const response = await axios.post(
        BASE_URL.url + `/leagues/${league.value}/matches`,
        { season_id: season.value, stage_id: stage.id },
        { headers }
      );
      const matchesData = Array.isArray(response.data) ? response.data : (response.data.data || []);
      setExistingMatches(matchesData.filter(m =>
        m.stage_id === stage.id || m.tour_id === stage.id
      ));
    } catch (error) {
      console.error('Error loading existing matches:', error);
      setExistingMatches([]);
    }
  };

  // Формат сезона: тип лиги и сколько туров идёт в группу — из настроек сезона.
  // league.league_type из списка лиг общий на лигу и для сезона может быть неверным
  const isGroupLeague = format ? format.leagueType === 2 : parseInt(league?.league_type) === 2;
  const groupStagesAmount = format ? format.groupStagesAmount : (parseInt(league?.group_stages_amount) || 0);

  const stagePriority = (stageId) => {
    const found = (stages || []).find(s => String(s.id) === String(stageId));
    return found && found.priority !== null && found.priority !== undefined ? Number(found.priority) : null;
  };
  // В групповой лиге пары считаются только по турам группового этапа
  const isCountedStage = (stageId) => {
    if (!isGroupLeague || !(groupStagesAmount > 0)) return true;
    const priority = stagePriority(stageId);
    return priority === null || priority <= groupStagesAmount;
  };
  const stageOrder = (stageId) => stagePriority(stageId) || 0;

  const listOf = (data) => (Array.isArray(data) ? data : (data && Array.isArray(data.data) ? data.data : []));
  const teamIdOf = (team) => String(team.team_id ?? team.id);

  // Состав групп — из league_groups (teamsByGroups); турнирная таблица — запасной вариант
  const loadGroupMap = async (headers) => {
    const map = {};
    try {
      const resp = await axios.post(
        BASE_URL.url + `/leagues/${league.value}/teamsByGroups`,
        { season_id: Number(season.value) },
        { headers }
      );
      listOf(resp.data).forEach(row => {
        if (row.team_id != null && row.group_name) map[String(row.team_id)] = row.group_name;
      });
    } catch (error) {
      console.error('Error loading groups:', error);
    }
    if (Object.keys(map).length > 0) return map;
    try {
      const resp = await axios.post(
        BASE_URL.url + `/leagues/${league.value}/v2/tournamentTable`,
        { season_id: parseInt(season.value) },
        { headers, timeout: 10000 }
      );
      const tableData = resp.data?.table_data || resp.data;
      if (Array.isArray(tableData)) {
        tableData.forEach(team => {
          const id = team.team_id || team.id;
          if (id && team.group_name) map[String(id)] = team.group_name;
        });
      }
    } catch (error) {
      // турнирная таблица недоступна
    }
    return map;
  };

  // Всё, что нужно для генерации, — каждый раз свежее с сервера
  const fetchPlanData = async () => {
    const headers = await getAuthHeaders();
    const [seasonResp, stageResp, teamsResp, groupOf] = await Promise.all([
      axios.post(BASE_URL.url + `/leagues/${league.value}/matches`, { season_id: season.value }, { headers }),
      axios.post(BASE_URL.url + `/leagues/${league.value}/matches`, { season_id: season.value, stage_id: stage.id }, { headers }),
      axios.post(BASE_URL.url + `/leagues/${league.value}/teams`, { season_id: season.value }, { headers }),
      isGroupLeague ? loadGroupMap(headers) : Promise.resolve(null),
    ]);
    // Матчи тура берём и из общего списка, и из отдельного запроса — без дублей
    const byId = new Map();
    [...listOf(seasonResp.data), ...listOf(stageResp.data)].forEach((m, index) => {
      byId.set(m.id != null ? `id${m.id}` : `i${index}`, m);
    });
    const allMatches = [...byId.values()];
    const teamsData = listOf(teamsResp.data);
    const teamIds = teamsData.map(teamIdOf);
    if (groupOf) {
      Object.keys(groupOf).forEach(id => { if (!teamIds.includes(id)) teamIds.push(id); });
    }
    return { allMatches, teamsData, teamIds, groupOf };
  };

  const planFor = (data, variantIndex) => planStage({
    teamIds: data.teamIds,
    groupOf: isGroupLeague ? data.groupOf : null,
    matches: data.allMatches,
    stageId: stage.id,
    isCountedStage,
    stageOrder,
    variant: variantIndex,
  });

  const teamNameIn = (data, id) => {
    const team = data.teamsData.find(t => teamIdOf(t) === String(id));
    return team ? team.name : `Команда ${id}`;
  };
  const teamImageIn = (data, id) => {
    const team = data.teamsData.find(t => teamIdOf(t) === String(id));
    return team ? (team.image || '') : '';
  };
  // id из ответа сервера бывают числами — отдаём в том же виде
  const rawId = (data, id) => {
    const team = data.teamsData.find(t => teamIdOf(t) === String(id));
    return team ? (team.team_id ?? team.id) : (isNaN(Number(id)) ? id : Number(id));
  };

  const groupLabel = (name) => (/^группа/i.test(name) ? name : `Группа ${name}`);

  // Превью и предупреждения из плана
  const applyPlan = (plan, data) => {
    const stadiumData = stadiums.find(s => s.value === selectedStadium);
    const judgeData = judges.find(j => j.value === selectedJudge);
    const selected = [];
    const notes = [];
    const names = (ids) => ids.map(id => teamNameIn(data, id)).join(', ');

    if (plan.skippedTeams.length > 0) {
      notes.push(`Не распределены по группам и в генерацию не попали: ${names(plan.skippedTeams)}.`);
    }
    plan.strayMatches.forEach(m => {
      notes.push(`В туре уже есть матч вне групп: ${teamNameIn(data, m.home_team_id)} — ${teamNameIn(data, m.guest_team_id)}. Его команды считаются занятыми.`);
    });

    plan.units.forEach(unit => {
      const prefix = unit.groupName ? `${groupLabel(unit.groupName)}: ` : '';
      if (unit.full) {
        if (unit.groupName) notes.push(`${prefix}тур уже заполнен (${unit.existing} из ${unit.perTour}).`);
        return;
      }
      const round = unit.round;
      if (unit.existing > 0) {
        notes.push(`${prefix}в туре уже ${unit.existing} из ${unit.perTour} матчей — досоздаются недостающие.`);
      }
      if (round.newCircle) {
        notes.push(`${prefix}все пары уже сыграли друг с другом — это ${round.circle}-й круг, пары повторятся.`);
      }
      if (!round.perfect && round.unmatched.length > 0) {
        notes.push(`${prefix}без соперника остаются ${names(round.unmatched)} — с доступными соперниками они уже сыграли в этом круге.`);
      }
      if (round.scheduleRisk) {
        notes.push(`${prefix}после этого тура круг без повторов уже не доиграть — проверьте пары в прошлых турах.`);
      }
      if (round.restingTeam) {
        notes.push(`${prefix}в этом туре отдыхает ${teamNameIn(data, round.restingTeam)}.`);
      }
      unit.pairs.forEach(pair => {
        selected.push({
          home_team_id: rawId(data, pair.home),
          guest_team_id: rawId(data, pair.guest),
          home_team_name: teamNameIn(data, pair.home),
          guest_team_name: teamNameIn(data, pair.guest),
          home_team_image: teamImageIn(data, pair.home),
          guest_team_image: teamImageIn(data, pair.guest),
          group_name: unit.groupName,
          played_before: pair.playedBefore,
        });
      });
    });

    const matchesWithSettings = selected.map((m, i) => ({
      ...m,
      date: matchDate,
      time: matchTime || null,
      stadium_id: selectedStadium,
      stadium_name: stadiumData?.name || '',
      judje_id: selectedJudge,
      judje_name: judgeData?.name || '',
      half_time: parseInt(halfTime) || 30,
      extra_time_available: league.cache_extra_time_available || false,
      extra_half_time: league.cache_extra_half_time || 15,
      penalty_kicks_available: league.cache_penalty_kicks_available || false,
      timeline_enabled: !league.cache_timeline_disabled,
      available: true,
      league_id: parseInt(league.value),
      league_name: league.name,
      season_id: parseInt(season.value),
      id: i
    }));

    setGeneratedMatches(matchesWithSettings);
    setDisabledMatches(new Set());
    setPlanNotes(notes);
    setVariantsCount(plan.variants);
    return matchesWithSettings.length;
  };

  /**
   * Генерация матчей тура (см. utilities/tourGenerator.js)
   */
  const generateMatches = async () => {
    if (!league || !season || !stage) {
      setMessage({ text: 'Выберите лигу, сезон и стадию', type: 'error' });
      return;
    }
    if (!selectedStadium) {
      setMessage({ text: 'Выберите стадион', type: 'error' });
      return;
    }
    if (!selectedJudge) {
      setMessage({ text: 'Выберите судью', type: 'error' });
      return;
    }
    if (!halfTime || parseInt(halfTime) <= 0) {
      setMessage({ text: 'Укажите время тайма', type: 'error' });
      return;
    }
    if (isGroupLeague && groupStagesAmount > 0 && Number(stage.priority) > groupStagesAmount) {
      setMessage({
        text: 'Это стадия плей-офф — генерация плей-офф пока не поддерживается, создайте матчи вручную. '
          + 'Если это групповой тур, нажмите у стадии «→ в группу» и сгенерируйте матчи снова.',
        type: 'error'
      });
      return;
    }

    setGenerating(true);
    setMessage({ text: '', type: '' });

    try {
      const data = await fetchPlanData();
      setExistingMatches(data.allMatches.filter(m => String(stageIdOf(m)) === String(stage.id)));

      if (isGroupLeague && Object.keys(data.groupOf || {}).length === 0) {
        setMessage({
          text: 'Команды сезона не распределены по группам. Распределите их: «Управление лигами» → «Настройки сезона» → «Группы».',
          type: 'error'
        });
        return;
      }

      const plan = planFor(data, 0);
      if (plan.full) {
        const details = plan.units
          .filter(u => u.groupName)
          .map(u => `${groupLabel(u.groupName)} — ${u.existing} из ${u.perTour}`)
          .join(', ');
        setMessage({
          text: `Тур уже заполнен: все матчи созданы${details ? ` (${details})` : ''}.`,
          type: 'info'
        });
        return;
      }

      setPlanData(data);
      setVariant(0);
      const count = applyPlan(plan, data);
      if (count === 0) {
        setMessage({
          text: 'Не удалось подобрать ни одной пары: все возможные соперники в этом круге уже сыграны или заняты в туре.',
          type: 'error'
        });
        return;
      }
      setStep('preview');
    } catch (error) {
      console.error('Error generating matches:', error);
      setMessage({
        text: error.response?.data?.message || 'Ошибка при генерации матчей',
        type: 'error'
      });
    } finally {
      setGenerating(false);
    }
  };

  /** Другой допустимый вариант пар для того же тура */
  const switchToNextVariant = () => {
    if (!planData || variantsCount <= 1) return;
    const next = (variant + 1) % variantsCount;
    setVariant(next);
    applyPlan(planFor(planData, next), planData);
  };

  /** Подтверждение и сохранение */
  const confirmAndSave = async () => {
    const toSave = generatedMatches.filter((m, i) => !disabledMatches.has(i) && m.home_team_id != null && m.guest_team_id != null);
    if (toSave.length === 0) {
      setMessage({ text: 'Нет матчей для сохранения. Включите хотя бы один матч.', type: 'error' });
      return;
    }

    setSaving(true);
    setMessage({ text: '', type: '' });

    try {
      // Пока открыт предпросмотр, кто-то мог создать матчи в этом туре
      // или эти же пары в другом — сверяемся со свежими данными
      const fresh = await fetchPlanData();
      const conflicts = findConflicts(toSave, planFor(fresh, variant));
      if (conflicts.length > 0) {
        setMessage({
          text: 'Пока был открыт предпросмотр, появились матчи с этими командами: '
            + conflicts.map(m => `${m.home_team_name} — ${m.guest_team_name}`).join(', ')
            + '. Ничего не сохранено — вернитесь назад и сгенерируйте заново.',
          type: 'error'
        });
        return;
      }

      const headers = await getAuthHeaders();
      await axios.post(
        BASE_URL.url + `/leagues/changeStage/${stage.id}`,
        {
          name: stage.name,
          short_name: stage.short_name,
          priority: stage.priority,
          group_id: stage.group_id,
          league_id: parseInt(league.value),
          season_id: parseInt(season.value),
          matches: toSave.map(({ played_before, ...match }) => match)
        },
        { headers }
      );

      setMessage({ text: `Успешно создано матчей: ${toSave.length}`, type: 'success' });
      if (onSuccess) onSuccess();
      setTimeout(() => { resetForm(); onClose(); }, 1500);
    } catch (error) {
      console.error('Error saving matches:', error);
      setMessage({
        text: error.response?.data?.message || 'Ошибка при сохранении матчей',
        type: 'error'
      });
    } finally {
      setSaving(false);
    }
  };

  const resetForm = () => {
    setStep('settings');
    setMatchDate(moment().format('YYYY-MM-DD'));
    setMatchTime('');
    setSelectedStadium('');
    setSelectedJudge('');
    setHalfTime('');
    setGeneratedMatches([]);
    setDisabledMatches(new Set());
    setPlanData(null);
    setVariant(0);
    setVariantsCount(0);
    setPlanNotes([]);
    setMessage({ text: '', type: '' });
  };

  const goBackToSettings = () => {
    setStep('settings');
    setGeneratedMatches([]);
    setDisabledMatches(new Set());
    setPlanData(null);
    setVariant(0);
    setVariantsCount(0);
    setPlanNotes([]);
    setMessage({ text: '', type: '' });
  };

  const toggleMatchDisabled = (index) => {
    setDisabledMatches((prevDisabled) => {
      const nextDisabled = new Set(prevDisabled);
      if (nextDisabled.has(index)) {
        nextDisabled.delete(index);
      } else {
        nextDisabled.add(index);
      }
      return nextDisabled;
    });
  };

  const fuzzySearch = (options) => {
    const fuse = new Fuse(options, { keys: ['name'], threshold: 0.3 });
    return (value) => {
      if (!value.length) return options;
      return fuse.search(value).map(r => r.item);
    };
  };

  if (!isOpen) return null;

  const enabledCount = generatedMatches.length - disabledMatches.size;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div
        className="modal-content create-match-modal"
        style={{ maxWidth: step === 'preview' ? '800px' : '500px' }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="modal-header">
          <h2>
            {step === 'settings' ? 'Сгенерировать матчи' : 'Превью сгенерированных матчей'}
          </h2>
          <button className="modal-close" onClick={onClose}>
            <FiX size={24} />
          </button>
        </div>

        <div className="modal-body">
          {message.text && (
            <div className={`message-box ${
              message.type === 'success' ? 'message-success' :
              message.type === 'error' ? 'message-error' :
              'message-info'
            }`}>
              {message.text}
            </div>
          )}

          <div className="info-section">
            <p><strong>Лига:</strong> {league?.name}</p>
            <p><strong>Сезон:</strong> {season?.name}</p>
            <p><strong>Стадия:</strong> {stage?.name}</p>
            {!format && (
              <p style={{ color: '#b7791f', fontSize: '13px' }}>
                <FiAlertCircle style={{ marginRight: '4px', verticalAlign: 'middle' }} />
                Настройки сезона не загрузились — тип лиги взят из общего списка лиг.
                Обновите страницу, если лига групповая, а здесь это не указано.
              </p>
            )}
            {isGroupLeague && (
              <p style={{ color: '#805ad5', fontSize: '13px' }}>
                <FiAlertCircle style={{ marginRight: '4px', verticalAlign: 'middle' }} />
                Групповая лига — матчи генерируются внутри групп
              </p>
            )}
            {existingMatches.length > 0 && (
              <p className="warning-text">
                <strong>Существующие матчи в туре:</strong> {existingMatches.length}
              </p>
            )}
          </div>

          {/* ============ ШАГ 1: НАСТРОЙКИ ============ */}
          {step === 'settings' && (
            <form onSubmit={(e) => { e.preventDefault(); generateMatches(); }}>
              <div className="form-section-modal">
                <label className="form-label-modal">Дата матчей</label>
                <input
                  className="input-field-modal"
                  type="date"
                  value={matchDate}
                  onChange={(e) => setMatchDate(e.target.value)}
                  required
                />
              </div>

              <div className="form-section-modal">
                <label className="form-label-modal">Время матчей (необязательно)</label>
                <input
                  className="input-field-modal"
                  type="time"
                  value={matchTime}
                  onChange={(e) => setMatchTime(e.target.value)}
                />
              </div>

              <div className="form-section-modal">
                <label className="form-label-modal">Стадион</label>
                <SelectSearch
                  options={stadiums}
                  value={selectedStadium}
                  search
                  filterOptions={fuzzySearch}
                  placeholder="Выберите стадион"
                  emptyMessage="Стадион не найден"
                  onChange={setSelectedStadium}
                />
              </div>

              <div className="form-section-modal">
                <label className="form-label-modal">Судья</label>
                <SelectSearch
                  options={judges}
                  value={selectedJudge}
                  search
                  filterOptions={fuzzySearch}
                  placeholder="Выберите судью"
                  emptyMessage="Судья не найден"
                  onChange={setSelectedJudge}
                />
              </div>

              <div className="form-section-modal">
                <label className="form-label-modal">Время тайма (мин)</label>
                <input
                  className="input-field-modal"
                  type="number"
                  min="1"
                  max="90"
                  value={halfTime}
                  onChange={(e) => setHalfTime(e.target.value)}
                  placeholder="Напр. 30 или 45"
                  required
                />
              </div>

              <div className="button-group-modal">
                <button
                  type="submit"
                  disabled={generating}
                  className="btn-modal btn-primary-modal"
                >
                  {generating ? 'Генерация...' : 'Сгенерировать матчи'}
                </button>
                <button
                  type="button"
                  onClick={onClose}
                  disabled={generating}
                  className="btn-modal btn-secondary-modal"
                >
                  Отмена
                </button>
              </div>
            </form>
          )}

          {/* ============ ШАГ 2: ПРЕВЬЮ ============ */}
          {step === 'preview' && (
            <div className="generate-preview">
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '10px' }}>
                <p style={{ fontSize: '14px', color: '#4a5568', margin: 0 }}>
                  Сгенерировано матчей: <strong>{generatedMatches.length}</strong>.
                  Включено: <strong>{enabledCount}</strong>.
                  Снимите галочку, чтобы исключить матч.
                </p>
                {variantsCount > 1 && (
                  <button
                    onClick={switchToNextVariant}
                    className="btn-icon"
                    style={{ 
                      background: 'none', 
                      border: '1px solid #e2e8f0', 
                      borderRadius: '6px', 
                      padding: '6px',
                      cursor: 'pointer',
                      color: '#4a5568'
                    }}
                    title={`Другой вариант пар (${variant + 1} из ${variantsCount})`}
                  >
                    <FiRefreshCw size={16} />
                  </button>
                )}
              </div>

              {planNotes.length > 0 && (
                <div className="generate-notes">
                  {planNotes.map((note, i) => <p key={i}>{note}</p>)}
                </div>
              )}

              <div className="generated-matches-list">
                {generatedMatches.map((match, index) => {
                  const isOff = disabledMatches.has(index);
                  const showGroupHeader = match.group_name && (
                    index === 0 || generatedMatches[index - 1].group_name !== match.group_name
                  );
                  return (
                    <React.Fragment key={index}>
                      {showGroupHeader && (
                        <div style={{
                          padding: '6px 12px',
                          margin: index > 0 ? '10px 0 4px' : '0 0 4px',
                          background: '#edf2f7',
                          borderRadius: '6px',
                          fontWeight: '600',
                          fontSize: '13px',
                          color: '#2d3748'
                        }}>
                          {groupLabel(match.group_name)}
                        </div>
                      )}
                      <div
                        className={`generated-match-row ${isOff ? 'match-disabled' : ''}`}
                      >
                      <label className="match-checkbox-label">
                        <input
                          type="checkbox"
                          checked={!isOff}
                          onChange={() => toggleMatchDisabled(index)}
                        />
                      </label>
                      <div className="match-pair-info">
                        <div className="match-team home-team">
                          {match.home_team_image && (
                            <img
                              src={match.home_team_image}
                              alt=""
                              className="team-logo-small"
                              onError={(e) => { e.target.style.display = 'none'; }}
                            />
                          )}
                          <span>{match.home_team_name}</span>
                          <span className="side-label home-label">дома</span>
                        </div>
                        <span className="match-vs">vs</span>
                        <div className="match-team away-team">
                          {match.guest_team_image && (
                            <img
                              src={match.guest_team_image}
                              alt=""
                              className="team-logo-small"
                              onError={(e) => { e.target.style.display = 'none'; }}
                            />
                          )}
                          <span>{match.guest_team_name}</span>
                          <span className="side-label away-label">гости</span>
                        </div>
                      </div>
                    </div>
                    </React.Fragment>
                  );
                })}
              </div>

              {/* Настройки матчей */}
              <div className="preview-settings-info">
                <p><strong>Дата:</strong> {moment(matchDate).format('DD.MM.YYYY')}</p>
                {matchTime && <p><strong>Время:</strong> {matchTime}</p>}
                <p><strong>Стадион:</strong> {stadiums.find(s => s.value === selectedStadium)?.name || '—'}</p>
                <p><strong>Судья:</strong> {judges.find(j => j.value === selectedJudge)?.name || '—'}</p>
                <p><strong>Время тайма:</strong> {halfTime} мин</p>
              </div>

              <div className="button-group-modal">
                <button
                  type="button"
                  onClick={goBackToSettings}
                  disabled={saving}
                  className="btn-modal btn-secondary-modal"
                  style={{ display: 'flex', alignItems: 'center', gap: '6px' }}
                >
                  <FiArrowLeft /> Назад
                </button>
                <button
                  type="button"
                  onClick={confirmAndSave}
                  disabled={saving || enabledCount === 0}
                  className="btn-modal btn-primary-modal"
                  style={{ display: 'flex', alignItems: 'center', gap: '6px', backgroundColor: '#10b981' }}
                >
                  {saving ? 'Сохранение...' : (
                    <>
                      <FiCheck /> Подтвердить ({enabledCount})
                    </>
                  )}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default GenerateTourModal;
